import { useEffect, useState } from "react";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge, Loading, EmptyState } from "@/components/Shared";
import ResetPasswordModal from "@/components/ResetPasswordModal";
import DocumentsModal from "@/components/DocumentsModal";
import { toast } from "sonner";
import { Users, Check, X, KeyRound, FileText } from "lucide-react";

export default function AdminPharmacists() {
  const [users, setUsers] = useState(null);
  const [tab, setTab] = useState("pending");
  const [resetUser, setResetUser] = useState(null);
  const [docsUser, setDocsUser] = useState(null);

  const load = () => api.get("/admin/users", { params: { role: "pharmacist" } }).then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const validate = async (id, status) => {
    try {
      await api.post(`/admin/users/${id}/validate`, { status });
      toast.success(status === "active" ? "Compte activé" : "Compte refusé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (users === null) return <Loading />;
  const filtered = users.filter((u) => (tab === "all" ? true : u.status === tab));

  return (
    <div>
      <PageHeader title="Pharmaciens" subtitle="Validez et gérez les comptes pharmaciens." />

      <div className="flex gap-2 mb-5">
        {[["pending", "En attente"], ["active", "Actifs"], ["all", "Tous"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${tab === v ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {l} {v === "pending" && <span className="num">({users.filter((u) => u.status === "pending").length})</span>}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? <EmptyState title="Aucun pharmacien" icon={Users} /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-5 py-3 font-semibold">Pharmacie</th>
                <th className="px-5 py-3 font-semibold">Responsable</th>
                <th className="px-5 py-3 font-semibold">Ville</th>
                <th className="px-5 py-3 font-semibold">Inscrit le</th>
                <th className="px-5 py-3 font-semibold">Statut</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`pharmacist-${u.id}`} className="hover:bg-slate-50 transition-colors duration-200">
                  <td className="px-5 py-3 font-semibold text-slate-900">{u.pharmacy_name || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{u.name}<div className="text-xs text-slate-400">{u.email}</div></td>
                  <td className="px-5 py-3 text-slate-500">{u.city || "—"}</td>
                  <td className="px-5 py-3 text-slate-400">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-3"><StatusBadge status={u.status} /></td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {u.status !== "active" && (
                        <button onClick={() => validate(u.id, "active")} data-testid={`approve-${u.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition-colors duration-200"><Check className="h-3.5 w-3.5" /> Valider</button>
                      )}
                      {u.status !== "rejected" && (
                        <button onClick={() => validate(u.id, "rejected")} data-testid={`reject-${u.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-semibold transition-colors duration-200"><X className="h-3.5 w-3.5" /> Refuser</button>
                      )}
                      <button onClick={() => setDocsUser(u)} data-testid={`docs-${u.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-semibold transition-colors duration-200"><FileText className="h-3.5 w-3.5" /> Pièces <span className="num">({u.documents_count || 0})</span></button>
                      <button onClick={() => setResetUser(u)} data-testid={`reset-${u.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold transition-colors duration-200"><KeyRound className="h-3.5 w-3.5" /> Dépanner</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {docsUser && <DocumentsModal user={docsUser} onClose={() => setDocsUser(null)} />}
      {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}
